import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Plus, MapPin, Calendar, ArrowRight, Film, Users, Radio, Clock, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import MMMonthlyReportDialog from "@/components/MMMonthlyReportDialog";

export default function AgentDashboard() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [requests, setRequests] = useState([]);
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reportOpen, setReportOpen] = useState(false);

    const isMM = user?.account_type === "marketing_manager";
    const accent = isMM ? "#7C3AED" : "#E5A93D";

    useEffect(() => {
        const p = [api.get("/requests").then((r) => setRequests(r.data))];
        if (isMM) {
            p.push(api.get("/marketing-manager/agents").then((r) => setAgents(r.data)).catch(() => {}));
        }
        Promise.all(p).finally(() => setLoading(false));
    }, [isMM]);

    const agentMap = useMemo(() => {
        const m = {};
        agents.forEach((a) => { m[a.id] = a; });
        return m;
    }, [agents]);

    const stats = {
        total: requests.length,
        pending: requests.filter((r) => r.status === "pending_approval").length,
        active: requests.filter((r) => ["approved_scheduled", "in_production"].includes(r.status)).length,
        delivered: requests.filter((r) => r.status === "delivered").length,
        live: requests.filter((r) => r.is_live).length,
    };

    // Sorting strategy — uniform across roles: nieuwste aangevraagd bovenaan.
    // Brokers + marketing managers willen beiden de meest recente projecten
    // eerst zien (intake-flow, recente leveringen, etc.). Voor "wat komt
    // eraan" is er een aparte Agenda-pagina.
    const sortedRequests = useMemo(() => {
        return [...requests].sort(
            (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0),
        );
    }, [requests]);

    const fmtDate = (d) => {
        if (!d) return "";
        const dt = typeof d === "string" ? new Date(d.length <= 10 ? `${d}T00:00:00` : d) : d;
        if (isNaN(dt)) return d;
        return dt.toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric" });
    };

    const headerTitle = isMM ? t("dash.title_mm") : (user?.account_type === "photographer" ? t("dash.title_photographer") : t("dash.title_agent"));
    const projectsHeading = isMM ? t("dash.heading_mm") : t("dash.heading_agent");
    const subtitle = isMM ? t("dash.sub_mm") : t("dash.sub_agent");

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 font-body pb-20" data-testid="agent-dashboard">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-10">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] font-semibold mb-3" style={{ color: accent }}>{headerTitle}</div>
                        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
                            {t("dash.hi")} <span style={{ color: accent }}>{user?.name?.split(" ")[0]}</span>.
                        </h1>
                        {subtitle && <p className="text-zinc-500 mt-2 text-sm">{subtitle}</p>}
                        {isMM && agents.length > 0 && (
                            <div className="mt-4 flex flex-wrap items-center gap-1.5" data-testid="managed-agents-chips">
                                <span className="text-[11px] uppercase tracking-wider text-zinc-500 flex items-center gap-1 mr-2">
                                    <Users className="h-3 w-3" /> {t("dash.linked")}
                                </span>
                                {agents.map((a) => (
                                    <span key={a.id} className="text-[11px] px-2 py-0.5 rounded-sm bg-[#7C3AED]/15 border border-[#7C3AED]/30 text-[#7C3AED]">
                                        {a.name}{a.company ? ` · ${a.company}` : ""}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    {!isMM && user?.account_type !== "photographer" && (
                        <Button
                            onClick={() => navigate("/new-request")}
                            className="h-11 px-6 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98]"
                            data-testid="new-request-btn"
                        >
                            <Plus className="mr-2 h-4 w-4" /> {t("dash.new_request")}
                        </Button>
                    )}
                    {isMM && agents.length > 0 && (
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                            <Button
                                onClick={() => setReportOpen(true)}
                                variant="ghost"
                                className="h-11 px-4 border border-zinc-200 text-zinc-800 hover:text-zinc-950 hover:bg-zinc-100 rounded-sm active:scale-[0.98]"
                                data-testid="mm-monthly-report-btn"
                            >
                                <FileDown className="mr-2 h-4 w-4" /> {t("dash.monthly_report")}
                            </Button>
                            <Button
                                onClick={() => navigate("/new-request")}
                                className="h-11 px-6 bg-[#7C3AED] hover:bg-[#8B5CF6] text-white font-bold rounded-sm active:scale-[0.98]"
                                data-testid="new-request-mm-btn"
                            >
                                <Plus className="mr-2 h-4 w-4" /> {t("dash.on_behalf")}
                            </Button>
                        </div>
                    )}
                    {isMM && agents.length === 0 && (
                        <div className="text-xs text-zinc-500 max-w-[260px] text-right" data-testid="mm-no-agents-hint">
                            {t("dash.no_agents_hint")}
                        </div>
                    )}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-12">
                    {[
                        { l: t("dash.stat_total"), v: stats.total },
                        { l: t("dash.stat_pending"), v: stats.pending },
                        { l: t("dash.stat_active"), v: stats.active },
                        { l: t("dash.stat_delivered"), v: stats.delivered },
                        { l: t("dash.stat_live"), v: stats.live, highlight: true },
                    ].map((s, i) => (
                        <motion.div
                            key={s.l}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.05 }}
                            className={`bg-[#FFFFFF] border rounded-md p-6 ${s.highlight ? "border-[#EF4444]/30" : "border-zinc-100"}`}
                            data-testid={`stat-card-${i}`}
                        >
                            <div className={`text-xs uppercase tracking-[0.2em] mb-2 flex items-center gap-1.5 ${s.highlight ? "text-[#EF4444]" : "text-zinc-500"}`}>
                                {s.highlight && <Radio className="h-3 w-3 animate-pulse" />}
                                {s.l}
                            </div>
                            <div className={`font-display font-black text-3xl ${s.highlight && s.v > 0 ? "text-[#EF4444]" : "text-zinc-950"}`}>{s.v}</div>
                        </motion.div>
                    ))}
                </div>

                {/* Projects List */}
                <div className="flex items-center justify-between mb-6">
                    <h2 className="font-display font-bold text-2xl tracking-tight">{projectsHeading}</h2>
                    {isMM && <span className="text-xs text-zinc-500">{requests.length} {requests.length === 1 ? t("dash.proj_one") : t("dash.proj_many")}</span>}
                </div>

                {loading ? (
                    <div className="text-center text-zinc-500 py-12">{t("dash.loading")}</div>
                ) : requests.length === 0 ? (
                    isMM ? (
                        <div className="border border-dashed border-zinc-200 rounded-md p-16 text-center" data-testid="mm-empty-state">
                            <div className="mx-auto w-14 h-14 rounded-full bg-[#7C3AED]/10 flex items-center justify-center mb-6">
                                <Film className="h-6 w-6 text-[#7C3AED]" />
                            </div>
                            <h3 className="font-display font-bold text-2xl mb-2">{t("dash.empty_title")}</h3>
                            <p className="text-zinc-500 max-w-md mx-auto">
                                {t("dash.mm_empty_body", { who: agents.length === 0 ? t("dash.mm_empty_linked") : t("dash.mm_empty_agents") })}
                            </p>
                        </div>
                    ) : (
                        <EmptyState onNew={() => navigate("/new-request")} />
                    )
                ) : (
                    <div className="space-y-3">
                        {sortedRequests.map((r, i) => {
                            const owner = agentMap[r.user_id];
                            const dateStr = r.scheduled_date || r.preferred_date;
                            const timeStr = r.scheduled_time || r.preferred_time;
                            return (
                                <motion.div
                                    key={r.id}
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.04 }}
                                    onClick={() => navigate(`/projects/${r.id}`)}
                                    className="group bg-[#FFFFFF] border border-zinc-100 hover:border-zinc-400 rounded-md p-6 cursor-pointer transition-all"
                                    data-testid={`project-row-${r.id}`}
                                >
                                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex flex-wrap items-center gap-3 mb-2">
                                                <StatusBadge status={r.status} />
                                                {r.is_live && (
                                                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-[#EF4444]/20 border border-[#EF4444]/40 text-[#EF4444]" data-testid={`live-badge-${r.id}`}>
                                                        <Radio className="h-2.5 w-2.5 animate-pulse" /> Live
                                                    </span>
                                                )}
                                                {isMM && owner && (
                                                    <span className="text-[11px] px-2 py-0.5 rounded-sm bg-[#7C3AED]/15 border border-[#7C3AED]/30 text-[#7C3AED]">
                                                        {owner.name}
                                                    </span>
                                                )}
                                                <span className="text-xs text-zinc-500">
                                                    {t("dash.requested")} {fmtDate(r.created_at)}
                                                </span>
                                            </div>
                                            <h3 className="font-display font-bold text-xl text-zinc-950 mb-2 group-hover:transition-colors" style={{ transition: "color .2s" }}>
                                                {r.address_street}, {r.address_city}
                                            </h3>
                                            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-zinc-500">
                                                <span className="flex items-center gap-1.5">
                                                    <Film className="h-3.5 w-3.5" /> {r.items.length} {r.items.length === 1 ? t("dash.product_one") : t("dash.product_many")}
                                                </span>
                                                <span className="inline-flex items-center gap-1.5" data-testid={`project-row-date-${r.id}`}>
                                                    <Calendar className="h-3.5 w-3.5" />
                                                    {r.date_pending || !dateStr ? (
                                                        <span className="italic text-zinc-500">{t("dash.date_tbd")}</span>
                                                    ) : (
                                                        <>
                                                            <span>{fmtDate(dateStr)}</span>
                                                            <Clock className="h-3.5 w-3.5 text-[#E5A93D]" />
                                                            <span>{timeStr}</span>
                                                        </>
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-6">
                                            {user?.account_type !== "photographer" && (
                                                <div className="text-right">
                                                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t("dash.total")}</div>
                                                    <div className="font-display font-black text-2xl" style={{ color: accent }}>€{r.total_price}</div>
                                                </div>
                                            )}
                                            <ArrowRight className="h-5 w-5 text-zinc-400 group-hover:translate-x-1 transition-all" style={{ color: "", transition: "color .2s, transform .2s" }} />
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </div>
            <MMMonthlyReportDialog open={reportOpen} onOpenChange={setReportOpen} />
        </div>
    );
}

function EmptyState({ onNew }) {
    const { t } = useTranslation();
    return (
        <div className="border border-dashed border-zinc-200 rounded-md p-16 text-center" data-testid="empty-state">
            <div className="mx-auto w-14 h-14 rounded-full bg-[#E5A93D]/10 flex items-center justify-center mb-6">
                <Film className="h-6 w-6 text-[#E5A93D]" />
            </div>
            <h3 className="font-display font-bold text-2xl mb-2">{t("dash.empty_title")}</h3>
            <p className="text-zinc-500 mb-8 max-w-md mx-auto">
                {t("dash.empty_body")}
            </p>
            <Button onClick={onNew} className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm" data-testid="empty-state-new-btn">
                <Plus className="mr-2 h-4 w-4" /> {t("dash.first_request")}
            </Button>
        </div>
    );
}
